import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, RotateCcw, Download } from "lucide-react";
import BootSequence from "../components/BootSequence";

/**
 * /intro — the official DEBSUN Boot Sequence as a standalone, reusable asset.
 * Full 6-second cinematic run, holds on Identity with Enter / Replay / Download controls.
 * Add ?clean=1 for a chrome-free capture (used to render the downloadable video).
 */
export default function IntroPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const clean = params.get("clean") === "1";
  const [runId, setRunId] = useState(0);
  const [done, setDone] = useState(false);

  return (
    <div className="fixed inset-0" style={{ background: "var(--navy)" }} data-testid="intro-page">
      <BootSequence
        key={runId}
        duration={6000}
        context="full"
        hold
        showSkip={false}
        hideChrome={clean}
        onDone={() => {}}
      />

      {!clean && (
        <IntroControls
          runId={runId}
          onReplay={() => { setDone(false); setRunId((n) => n + 1); }}
          onEnter={() => nav("/")}
          setDone={setDone}
          done={done}
        />
      )}
    </div>
  );
}

function IntroControls({ runId, onReplay, onEnter, setDone, done }) {
  React.useEffect(() => {
    setDone(false);
    const t = setTimeout(() => setDone(true), 6200);
    return () => clearTimeout(t);
  }, [runId, setDone]);

  return (
    <AnimatePresence>
      {done && (
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="absolute inset-x-0 bottom-24 z-[210] flex items-center justify-center gap-3 flex-wrap px-6"
        >
          <button onClick={onEnter} data-testid="intro-enter-btn"
            className="btn-gold clip-frame-sm px-7 py-4 text-xs uppercase tracking-[0.2em] font-mono-2 inline-flex items-center gap-2">
            Enter Site <ArrowRight size={15} />
          </button>
          <button onClick={onReplay} data-testid="intro-replay-btn"
            className="clip-frame-sm px-7 py-4 text-xs uppercase tracking-[0.2em] font-mono-2 inline-flex items-center gap-2 border border-white/20 text-[color:var(--console-text)]/80 hover:text-[color:var(--paper)] hover:border-[color:var(--gold)] transition-colors">
            <RotateCcw size={14} /> Replay
          </button>
          <div className="w-full basis-full h-0" />
          <a href="/brand/debsun-intro.mp4" download data-testid="intro-download-mp4"
            className="clip-frame-sm px-5 py-3 text-[10px] uppercase tracking-[0.2em] font-mono-2 inline-flex items-center gap-2 border border-white/15 text-[color:var(--console-text)]/70 hover:text-[color:var(--gold)] hover:border-[color:var(--gold)] transition-colors">
            <Download size={13} /> MP4
          </a>
          <a href="/brand/debsun-intro.webm" download data-testid="intro-download-webm"
            className="clip-frame-sm px-5 py-3 text-[10px] uppercase tracking-[0.2em] font-mono-2 inline-flex items-center gap-2 border border-white/15 text-[color:var(--console-text)]/70 hover:text-[color:var(--gold)] hover:border-[color:var(--gold)] transition-colors">
            <Download size={13} /> WebM
          </a>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
